import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit, FiCheck, FiSearch, FiX, FiPackage, FiUpload, FiDownload, FiClock, FiAlertCircle, FiEye, FiTrash2, FiFilter, FiGrid, FiList } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const MenuManagement = () => {
  const [activeTab, setActiveTab] = useState('items');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickCreateType, setQuickCreateType] = useState('');
  const [quickCategoryName, setQuickCategoryName] = useState('');
  const [quickSubCategoryName, setQuickSubCategoryName] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [selectedSubCategoryFilter, setSelectedSubCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryItemsPerPage, setCategoryItemsPerPage] = useState(10);
  const [subCategoryPage, setSubCategoryPage] = useState(1);
  const [subCategoryItemsPerPage, setSubCategoryItemsPerPage] = useState(10);

  // Recipe Management States
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedInventoryCategory, setSelectedInventoryCategory] = useState('');
  const [recipeItems, setRecipeItems] = useState([]);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [selectedMenuItemForRecipe, setSelectedMenuItemForRecipe] = useState(null);
  const [loadingInventory, setLoadingInventory] = useState(false);

  // CSV Import States
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const fileInputRef = useRef(null);
  const imageUploadRef = useRef(null);
  const [uploadingImageFor, setUploadingImageFor] = useState(null);

  // Approval Status States
  const [pendingCount, setPendingCount] = useState({ pendingCreation: 0, pendingUpdates: 0, pendingDeletes: 0, total: 0 });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatusItem, setSelectedStatusItem] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Image Management States
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [imageSearchTerm, setImageSearchTerm] = useState('');
  const [imageViewMode, setImageViewMode] = useState('grid');
  const [selectedImages, setSelectedImages] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingImage, setRenamingImage] = useState(null);
  const [newFilename, setNewFilename] = useState('');
  const [showUnusedImages, setShowUnusedImages] = useState(false);
  const [unusedImages, setUnusedImages] = useState([]);
  const [loadingUnused, setLoadingUnused] = useState(false);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    gstPercentage: 5,
    sortOrder: 0,
    isActive: true,
    isGSTInclude: false
  });

  const [subCategoryForm, setSubCategoryForm] = useState({
    categoryId: '',
    name: '',
    description: '',
    sortOrder: 0,
    isActive: true
  });

  const [menuItemForm, setMenuItemForm] = useState({
    categoryId: '',
    subCategoryId: '',
    name: '',
    description: '',
    price: '',
    pricingUnit: '',
    discountPrice: '',
    isVeg: true,
    isAvailable: true,
    preparationTime: 15,
    sortOrder: 0,
    image: null,
    itemCode: ''
  });


  // Listen for custom event to open Add Order modal (from global Ctrl+A shortcut)
  useEffect(() => {
    const handleOpenAddCategoryModal = () => {
      openModal('category')
    };

    const handleOpenAddSubCategoryModal = () => {
      openModal('subcategory')
    };

    const handleOpenAddMenuItemModal = () => {
      openModal('item')
    };

    window.addEventListener('openAddCategoryModal', handleOpenAddCategoryModal);
    window.addEventListener('openAddSubCategoryModal', handleOpenAddSubCategoryModal);
    window.addEventListener('openAddMenuItemModal', handleOpenAddMenuItemModal);

    return () => {
      window.removeEventListener('openAddCategoryModal', handleOpenAddCategoryModal);
      window.removeEventListener('openAddSubCategoryModal', handleOpenAddSubCategoryModal);
      window.removeEventListener('openAddMenuItemModal', handleOpenAddMenuItemModal);
    };
  }, []);

  useEffect(() => {
    fetchData();
    fetchPendingCount();
  }, []);

  useEffect(() => {
    fetchPendingCount();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (activeTab === 'items') {
      fetchMenuItems();
    } else if (activeTab === 'images') {
      fetchImages();
    }
  }, [currentPage, itemsPerPage, selectedCategoryFilter, selectedSubCategoryFilter, searchTerm, activeTab]);

  useEffect(() => {
    // Reset subcategory when category changes
    if (menuItemForm.categoryId) {
      const availableSubCats = subCategories.filter(
        sc => (sc.categoryId?._id || sc.categoryId) === menuItemForm.categoryId
      );
      if (availableSubCats.length > 0 && !menuItemForm.subCategoryId) {
        // Don't reset if subcategory is already set
      } else if (availableSubCats.length === 0) {
        setMenuItemForm({ ...menuItemForm, subCategoryId: '' });
      }
    }
  }, [menuItemForm.categoryId, subCategories]);

  useEffect(() => {
    fetchInventoryCategories();
  }, []);

  useEffect(() => {
    if (selectedInventoryCategory) {
      fetchInventoryItems();
    }
  }, [selectedInventoryCategory]);

  // Image Management Functions
  const fetchImages = async () => {
    try {
      setLoadingImages(true);
      const response = await api.get('/vendor/images');
      setImages(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch images');
      console.error('Error fetching images:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchUnusedImages = async () => {
    try {
      setLoadingUnused(true);
      const response = await api.get('/vendor/images/unused');
      setUnusedImages(response.data.data || []);
      setShowUnusedImages(true);
    } catch (error) {
      toast.error('Failed to fetch unused images');
      console.error('Error fetching unused images:', error);
    } finally {
      setLoadingUnused(false);
    }
  };

  const handleImageManagementUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Validate files
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 1MB`);
        return;
      }
    }

    setUploadingImages(true);
    const uploadPromises = files.map(file => {
      const formData = new FormData();
      formData.append('image', file);
      return api.post('/vendor/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    });

    try {
      await Promise.all(uploadPromises);
      toast.success(`${files.length} image(s) uploaded successfully`);
      fetchImages();
      setShowUploadModal(false);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await api.delete(`/vendor/images/${imageId}`);
      toast.success('Image deleted successfully');
      
      // Refresh the appropriate view
      if (showUnusedImages) {
        await fetchUnusedImages();
      } else {
        await fetchImages();
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select images to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedImages.length} image(s)?`)) {
      return;
    }

    try {
      const deletePromises = selectedImages.map(imageId => 
        api.delete(`/vendor/images/${imageId}`)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedImages.length} image(s) deleted successfully`);
      setSelectedImages([]);
      
      // Refresh the appropriate view
      if (showUnusedImages) {
        await fetchUnusedImages();
      } else {
        await fetchImages();
      }
    } catch (error) {
      toast.error('Failed to delete images');
    }
  };

  const handleRenameImage = async (image) => {
    if (!newFilename.trim()) {
      toast.error('Please enter a filename');
      return;
    }

    // Add file extension if not present
    const finalFilename = newFilename.includes('.') ? newFilename : 
      `${newFilename}.${image.filename.split('.').pop()}`;

    try {
      await api.put(`/vendor/images/${image._id}/rename`, { 
        filename: finalFilename 
      });
      toast.success('Image renamed successfully');
      setShowRenameModal(false);
      setNewFilename('');
      setRenamingImage(null);
      fetchImages();
    } catch (error) {
      toast.error('Failed to rename image');
    }
  };

  const handleDownload = async (image) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = image.originalName || image.filename || 'image';

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const toggleAllImageSelection = () => {
    const currentImages = showUnusedImages ? unusedImages : images;
    const allSelected = currentImages.every(img => selectedImages.includes(img._id));
    
    if (allSelected) {
      setSelectedImages([]);
    } else {
      setSelectedImages(currentImages.map(img => img._id));
    }
  };

  const filteredImages = (showUnusedImages ? unusedImages : images).filter(image =>
    image.filename.toLowerCase().includes(imageSearchTerm.toLowerCase()) ||
    image.originalName?.toLowerCase().includes(imageSearchTerm.toLowerCase())
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, subCatRes] = await Promise.all([
        api.get('/vendor/categories'),
        api.get('/vendor/sub-categories')
      ]);

      setCategories(catRes.data.data || []);
      setSubCategories(subCatRes.data.data || []);

      await fetchMenuItems();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      if (selectedCategoryFilter !== 'all') params.categoryId = selectedCategoryFilter;
      if (selectedSubCategoryFilter !== 'all') params.subCategoryId = selectedSubCategoryFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await api.get('/vendor/menu-items', { params });
      if (response.data.success) {
        setMenuItems(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to fetch menu items');
    }
  };

  const fetchInventoryCategories = async () => {
    try {
      const response = await api.get('/vendor/inventory-categories');
      if (response.data.success) {
        setInventoryCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch inventory categories:', error);
    }
  };

  const fetchInventoryItems = async () => {
    if (!selectedInventoryCategory) return;

    setLoadingInventory(true);
    try {
      const response = await api.get(`/vendor/inventory-items?categoryId=${selectedInventoryCategory}`);
      if (response.data.success) {
        setInventoryItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const fetchInventoryItemsByCategory = async (categoryId) => {
    if (!categoryId) return;

    setLoadingInventory(true);
    try {
      const response = await api.get(`/vendor/inventory-items?categoryId=${categoryId}`);
      if (response.data.success) {
        setInventoryItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await api.put(`/vendor/categories/${selectedItem._id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        const response = await api.post('/vendor/categories', categoryForm);
        toast.success('Category created successfully');
        if (showQuickCreate) {
          const newCategory = response.data.data;
          if (quickCreateType === 'forSubCategory') {
            setSubCategoryForm({ ...subCategoryForm, categoryId: newCategory._id });
          } else if (quickCreateType === 'forMenuItem') {
            setMenuItemForm({ ...menuItemForm, categoryId: newCategory._id });
          }
        }
      }
      setShowModal(false);
      setShowQuickCreate(false);
      setQuickCreateType('');
      resetForms();
      fetchData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleQuickCreateCategory = async () => {
    if (!quickCategoryName.trim()) {
      toast.error('Please enter category name');
      return;
    }
    try {
      const response = await api.post('/vendor/categories', {
        name: quickCategoryName,
        description: '',
        gstPercentage: 5,
        sortOrder: 0,
        isActive: true,
        isGSTInclude: false
      });
      const newCategory = response.data.data;
      toast.success('Category created successfully');
      await fetchData();

      if (modalType === 'subcategory') {
        setSubCategoryForm({ ...subCategoryForm, categoryId: newCategory._id });
      } else if (modalType === 'item') {
        setMenuItemForm({ ...menuItemForm, categoryId: newCategory._id });
      }
      setQuickCategoryName('');
      setShowQuickCreate(false);
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleQuickCreateSubCategory = async () => {
    if (!quickSubCategoryName.trim()) {
      toast.error('Please enter subcategory name');
      return;
    }
    if (!menuItemForm.categoryId) {
      toast.error('Please select a category first');
      return;
    }
    try {
      // Store the current categoryId to preserve it after fetchData
      const currentCategoryId = menuItemForm.categoryId;

      const response = await api.post('/vendor/sub-categories', {
        categoryId: currentCategoryId,
        name: quickSubCategoryName,
        description: '',
        sortOrder: 0,
        isActive: true
      });
      const newSubCategory = response.data.data;
      toast.success('Sub-category created successfully');
      await fetchData();

      // Restore the category and set the new subcategory
      setMenuItemForm({
        ...menuItemForm,
        categoryId: currentCategoryId,
        subCategoryId: newSubCategory._id
      });
      setQuickSubCategoryName('');
      setShowQuickCreate(false);
      setQuickCreateType('');
    } catch (error) {
      toast.error('Failed to create sub-category');
    }
  };

  // Approval Status Functions
  const fetchPendingCount = async () => {
    try {
      const response = await api.get('/vendor/menu-items/pending-count');
      if (response.data.success) {
        setPendingCount(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch pending count:', error);
    }
  };

  const fetchItemStatus = async (menuItemId) => {
    setLoadingStatus(true);
    try {
      const response = await api.get(`/vendor/menu-items/${menuItemId}/status`);
      if (response.data.success) {
        setSelectedStatusItem(response.data.data);
        setShowStatusModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch item status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const getApprovalStatusBadge = (item) => {
    if (item.hasPendingDelete) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex items-center gap-1">
          <FiAlertCircle size={10} />
          Pending Delete
        </span>
      );
    }
    if (item.hasPendingUpdate) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 flex items-center gap-1">
          <FiClock size={10} />
          Pending Update
        </span>
      );
    }
    if (!item.isApproved) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <FiClock size={10} />
          Pending Approval
        </span>
      );
    }
    return (
      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1">
        <FiCheck size={10} />
        Approved
      </span>
    );
  };

  const handleCreateSubCategory = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await api.put(`/vendor/sub-categories/${selectedItem._id}`, subCategoryForm);
        toast.success('Sub-category updated successfully');
      } else {
        await api.post('/vendor/sub-categories', subCategoryForm);
        toast.success('Sub-category created successfully');
      }
      setShowModal(false);
      resetForms();
      fetchData();
    } catch (error) {
      toast.error('Failed to save sub-category');
    }
  };

  const handleCreateMenuItem = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('categoryId', menuItemForm.categoryId);
      if (menuItemForm.subCategoryId) formData.append('subCategoryId', menuItemForm.subCategoryId);
      formData.append('name', menuItemForm.name);
      formData.append('description', menuItemForm.description);
      formData.append('price', menuItemForm.price);
      if (menuItemForm.pricingUnit) formData.append('pricingUnit', menuItemForm.pricingUnit);
      if (menuItemForm.discountPrice) formData.append('discountPrice', menuItemForm.discountPrice);
      formData.append('isVeg', menuItemForm.isVeg);
      formData.append('isAvailable', menuItemForm.isAvailable);
      formData.append('preparationTime', menuItemForm.preparationTime);
      formData.append('sortOrder', menuItemForm.sortOrder);
      if (menuItemForm.itemCode && menuItemForm.itemCode.trim()) {
        formData.append('itemCode', menuItemForm.itemCode);
      }


      if (menuItemForm.image) {
        formData.append('images', menuItemForm.image);
      }

      if (selectedItem) {
        await api.put(`/vendor/menu-items/${selectedItem._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Menu item update submitted successfully and is pending approval from Vendor Owner');
      } else {
        await api.post('/vendor/menu-items', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Menu item created successfully and is pending approval from Vendor Owner');
      }
      setShowModal(false);
      resetForms();
      fetchData();
      fetchPendingCount(); // Refresh pending count
    } catch (error) {
      toast.error(error.response.data.message || 'Failed to save menu item');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      if (type === 'category') {
        await api.delete(`/vendor/categories/${id}`);
      } else if (type === 'subcategory') {
        await api.delete(`/vendor/sub-categories/${id}`);
      } else {
        await api.delete(`/vendor/menu-items/${id}`);
        toast.success('Menu item delete request submitted successfully and is pending approval from Vendor Owner');
        fetchPendingCount(); // Refresh pending count
        return; // Don't show general success message for menu items
      }
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Recipe Management Functions
  const openRecipeModal = (menuItem) => {
    if (!menuItem || !menuItem._id) {
      toast.error('Invalid menu item selected');
      return;
    }

    console.log('Opening recipe modal for:', menuItem); // Debug log
    setSelectedMenuItemForRecipe(menuItem);
    setRecipeItems([]);
    setSelectedInventoryCategory('');
    setInventoryItems([]);
    setShowRecipeModal(true);

    // If menu item has existing recipe, load it
    if (menuItem?.recipe?.ingredients?.length > 0) {
      console.log('Loading existing recipe ingredients:', menuItem.recipe.ingredients);
      setRecipeItems(menuItem.recipe.ingredients);
      // Set the category to the first ingredient's category
      const firstIngredient = menuItem.recipe.ingredients[0];
      const categoryId = firstIngredient.categoryId?._id || firstIngredient.categoryId;
      if (categoryId) {
        setSelectedInventoryCategory(categoryId);
        // Fetch items for that category
        fetchInventoryItemsByCategory(categoryId);
      }
    }
  };

  const closeRecipeModal = () => {
    setShowRecipeModal(false);
    setSelectedMenuItemForRecipe(null);
    setRecipeItems([]);
    setSelectedInventoryCategory('');
    setInventoryItems([]);
  };

  // Unit conversion function
  const convertUnit = (originalUnit, originalQuantity) => {
    const conversionMap = {
      // Large to small units
      'kg': { target: 'g', factor: 1000 },
      'l': { target: 'ml', factor: 1000 },
      'dozen': { target: 'pcs', factor: 12 },
      'packet': { target: 'pcs', factor: 1 }, // 1 packet = 1 pcs (can be adjusted based on needs)

      // Small units remain the same (no conversion needed)
      'g': { target: 'g', factor: 1 },
      'ml': { target: 'ml', factor: 1 },
      'pcs': { target: 'pcs', factor: 1 }
    };

    const conversion = conversionMap[originalUnit?.toLowerCase()];
    if (conversion && conversion.factor > 1) {
      return {
        unit: conversion.target,
        quantity: originalQuantity * conversion.factor
      };
    }

    return {
      unit: originalUnit,
      quantity: originalQuantity
    };
  };

  // Get conversion options for radio buttons
  const getConversionOptions = (originalUnit) => {
    const unitOptions = {
      'kg': [
        { value: 'kg', label: 'kg' },
        { value: 'g', label: 'g', factor: 1000 }
      ],
      'g': [
        { value: 'kg', label: 'kg', factor: 0.001 },
        { value: 'g', label: 'g', factor: 1 }
      ],
      'l': [
        { value: 'l', label: 'l' },
        { value: 'ml', label: 'ml', factor: 1000 }
      ],
      'ml': [
        { value: 'l', label: 'l', factor: 0.001 },
        { value: 'ml', label: 'ml', factor: 1 }
      ],
      'dozen': [
        { value: 'dozen', label: 'dozen' },
        { value: 'pcs', label: 'pcs', factor: 12 }
      ],
      'pcs': [
        { value: 'dozen', label: 'dozen', factor: 0.08333 }, // 1 pcs = 1/12 dozen
        { value: 'pcs', label: 'pcs', factor: 1 }
      ]
      // packet and other units have no conversion options
    };

    return unitOptions[originalUnit?.toLowerCase()] || [
      { value: originalUnit, label: originalUnit, factor: 1 }
    ];
  };

  const addRecipeItem = (inventoryItem) => {
    const existingItem = recipeItems.find(item => item.itemId === inventoryItem._id);
    if (existingItem) {
      toast.error('This item is already added to the recipe');
      return;
    }

    if (!selectedInventoryCategory) {
      toast.error('Please select a category first');
      return;
    }

    // Ensure categoryId is a string, not an object
    const categoryId = typeof selectedInventoryCategory === 'object'
      ? selectedInventoryCategory._id
      : selectedInventoryCategory;

    // Start with original unit (larger unit) as default
    const newIngredient = {
      categoryId: categoryId,
      itemId: inventoryItem._id,
      name: inventoryItem.name,
      quantity: 1,
      unit: inventoryItem.unit || 'pcs', // Keep original unit as default
      stock: inventoryItem.currentStock || inventoryItem.stock || 0,
      originalUnit: inventoryItem.unit || 'pcs' // Keep original unit for reference
    };

    // console.log('Adding ingredient:', newIngredient); // Debug log
    setRecipeItems([...recipeItems, newIngredient]);
  };

  const removeRecipeItem = (itemId) => {
    setRecipeItems(recipeItems.filter(item => item.itemId !== itemId));
  };

  const updateRecipeItem = (itemId, field, value) => {
    setRecipeItems(recipeItems.map(item =>
      item.itemId === itemId ? { ...item, [field]: value } : item
    ));
  };

  const saveRecipe = async () => {
    if (recipeItems.length === 0) {
      toast.error('Please add at least one ingredient to the recipe');
      return;
    }

    if (!selectedMenuItemForRecipe || !selectedMenuItemForRecipe._id) {
      toast.error('Menu item not selected');
      return;
    }

    try {
      const recipeData = {
        menuItemId: selectedMenuItemForRecipe._id,
        ingredients: recipeItems
      };

      console.log('Sending recipe data:', recipeData); // Debug log

      const response = await api.post('/vendor/menu-items/recipe', recipeData);
      if (response.data.success) {
        toast.success('Recipe saved successfully!');
        closeRecipeModal();
        // Update the selected item with the new recipe data
        if (selectedMenuItemForRecipe) {
          setSelectedItem(response.data.data);
        }
        // Refresh menu items to show updated recipe status
        fetchMenuItems();
      }
    } catch (error) {
      console.error('Recipe save error:', error);
      toast.error('Failed to save recipe');
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);

    if (item) {
      if (type === 'category') {
        setCategoryForm(item);
      } else if (type === 'subcategory') {
        setSubCategoryForm({
          ...item,
          categoryId: item.categoryId?._id || item.categoryId || ''
        });
      } else {
        setMenuItemForm({
          ...item,
          categoryId: item.categoryId?._id || item.categoryId || '',
          subCategoryId: item.subCategoryId?._id || item.subCategoryId || ''
        });
      }
    }
    setShowModal(true);
  };

  const resetForms = () => {
    setCategoryForm({
      name: '',
      description: '',
      gstPercentage: 5,
      sortOrder: 0,
      isActive: true,
      isGSTInclude: false
    });
    setSubCategoryForm({
      categoryId: selectedCategoryFilter || '',
      name: '',
      description: '',
      sortOrder: 0,
      isActive: true
    });
    setMenuItemForm({
      categoryId: selectedCategoryFilter || '',
      subCategoryId: selectedSubCategoryFilter || '',
      name: '',
      description: '',
      price: '',
      pricingUnit: '',
      discountPrice: '',
      isVeg: true,
      isAvailable: true,
      preparationTime: 15,
      sortOrder: 0,
      image: null,
      itemCode: ''
    });
    setSelectedItem(null);
  };

  const filteredItems = menuItems;

  const filteredSubCategories = selectedCategoryFilter === 'all'
    ? []
    : subCategories.filter(sc => (sc.categoryId?._id || sc.categoryId) === selectedCategoryFilter);

  // CSV Import Functions
  const downloadSampleCSV = () => {
    const csvContent = [
      ['Category', 'Sub Category', 'Item Name', 'Description', 'Price', 'Discount Price', 'itemCode', 'Is Veg', 'Is Available', 'Preparation Time (minutes)', 'Sort Order', 'Image'],
      ['Beverages', 'Hot Drinks', 'Tea', 'Freshly brewed tea', 25, 20, '="001"', 'true', 'true', 5, 1, 'tea.png'],
      ['Beverages', 'Cold Drinks', 'Cold Coffee', 'Chilled coffee with ice', 45, 40, '="002"', 'true', 'true', 8, 2, 'cold-coffee.jpg'],
      ['Food', '', 'Sandwich', 'Fresh vegetable sandwich', 60, 55, '="003"', 'false', 'false', 10, 1, 'sandwich.png'],
    ];

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'menu-import-sample.csv';
    link.click();
    toast.success('Sample CSV downloaded');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  // Handle image click to upload/edit
  const handleImageClick = (item) => {
    setUploadingImageFor(item);
    imageUploadRef.current?.click();
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, JPG, PNG, WebP, GIF)');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Image size should be less than 1MB');
      return;
    }

    if (!uploadingImageFor) return;

    // Show loading toast
    const loadingToast = toast.loading('Uploading image...');

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Use dedicated image-only update endpoint
      await api.patch(`/vendor/menu-items/${uploadingImageFor._id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Image uploaded successfully');
      fetchMenuItems();
      setUploadingImageFor(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload image';
      toast.error(errorMessage);
    } finally {
      // Dismiss loading toast
      toast.dismiss(loadingToast);
    }

    // Reset file input
    event.target.value = '';
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        data.push(row);
      }
    }

    return data;
  };

  const importMenuItems = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const text = await csvFile.text();
      const csvData = parseCSV(text);

      if (csvData.length === 0) {
        toast.error('No data found in CSV file');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Track created categories and subcategories to avoid duplicates
      const createdCategories = new Map();
      const createdSubCategories = new Map();

      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];

        try {
          // Validate mandatory fields
          if (!row['Item Name'] || !row['Item Name'].trim()) {
            errorCount++;
            errors.push({
              row: i + 1,
              itemName: row['Item Name'] || 'Unknown',
              category: row['Category'] || 'Unknown',
              subCategory: row['Sub Category'] || 'None',
              itemCode: row['itemCode'] || 'N/A',
              price: row['Price'] || '0',
              error: 'Item Name is required'
            });
            continue;
          }

          if (!row['itemCode'] || !row['itemCode'].trim()) {
            errorCount++;
            errors.push({
              row: i + 1,
              itemName: row['Item Name'] || 'Unknown',
              category: row['Category'] || 'Unknown',
              subCategory: row['Sub Category'] || 'None',
              itemCode: row['itemCode'] || 'N/A',
              price: row['Price'] || '0',
              error: 'itemCode is required'
            });
            continue;
          }

          if (!row['Category'] || !row['Category'].trim()) {
            errorCount++;
            errors.push({
              row: i + 1,
              itemName: row['Item Name'] || 'Unknown',
              category: row['Category'] || 'Unknown',
              subCategory: row['Sub Category'] || 'None',
              itemCode: row['itemCode'] || 'N/A',
              price: row['Price'] || '0',
              error: 'Category is required'
            });
            continue;
          }

          // Find or create category
          let category = categories.find(cat =>
            cat.name.toLowerCase() === row['Category']?.toLowerCase()
          );

          if (!category && row['Category']) {
            // Check if we already created this category in this import session
            if (createdCategories.has(row['Category'])) {
              category = createdCategories.get(row['Category']);
            } else {
              const categoryResponse = await api.post('/vendor/categories', {
                name: row['Category'],
                description: '',
                gstPercentage: 5,
                sortOrder: 0,
                isActive: true,
                isGSTInclude: false
              });
              category = categoryResponse.data.data;
              createdCategories.set(row['Category'], category);
            }
          }

          // Find or create subcategory (optional)
          let subCategory = null;
          if (row['Sub Category'] && row['Sub Category'].trim() && category) {
            subCategory = subCategories.find(sub =>
              sub.name.toLowerCase() === row['Sub Category']?.toLowerCase() &&
              (sub.categoryId?._id || sub.categoryId) === category._id
            );

            if (!subCategory) {
              const subCategoryKey = `${category._id}-${row['Sub Category'].trim()}`;

              // Check if we already created this subcategory in this import session
              if (createdSubCategories.has(subCategoryKey)) {
                subCategory = createdSubCategories.get(subCategoryKey);
              } else {
                const subCategoryResponse = await api.post('/vendor/sub-categories', {
                  categoryId: category._id,
                  name: row['Sub Category'].trim(),
                  description: '',
                  sortOrder: 0,
                  isActive: true
                });
                subCategory = subCategoryResponse.data.data;
                createdSubCategories.set(subCategoryKey, subCategory);
              }
            }
          }

          // Create menu item with FormData
          const formData = new FormData();
          formData.append('categoryId', category?._id || '');
          if (subCategory?._id) {
            formData.append('subCategoryId', subCategory?._id);
          }
          formData.append('name', row['Item Name'] || '');
          formData.append('description', row['Description'] || '');
          formData.append('price', parseFloat(row['Price']) || 0);
          formData.append('discountPrice', parseFloat(row['Discount Price']) || 0);
          formData.append('itemCode', row['itemCode'] || '');
          formData.append('isVeg', row['Is Veg']?.toLowerCase() === 'true');
          formData.append('isAvailable', row['Is Available']?.toLowerCase() === 'true');
          formData.append('preparationTime', parseInt(row['Preparation Time (minutes)']) || 15);
          formData.append('sortOrder', parseInt(row['Sort Order']) || 0);

          // Handle image if provided
          if (row['Image'] && row['Image'].trim()) {
            const imageFilename = row['Image'].trim();
            // Only send the filename, backend will construct the full path
            formData.append('imageFilename', imageFilename);
          }

          await api.post('/vendor/menu-items', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          successCount++;
        } catch (error) {
          errorCount++;
          const errorMessage = error.response?.data?.message || error.message;
          errors.push({
            row: i + 1,
            itemName: row['Item Name'] || 'Unknown',
            category: row['Category'] || 'Unknown',
            subCategory: row['Sub Category'] || 'None',
            itemCode: row['itemCode'] || 'N/A',
            price: row['Price'] || '0',
            error: errorMessage
          });
        }
      }

      // Single refresh at the end
      if (successCount > 0) {
        toast.success(`${successCount} items imported successfully`);
        await fetchData(); // Refresh all data once
        await fetchMenuItems();
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} items failed to import`);
        setImportErrors(errors);
        setShowErrorModal(true);
      }

      setShowImportModal(false);
      setCsvFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Failed to import menu items');
      console.error('Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hidden file input for image upload */}
      <input
        type="file"
        ref={imageUploadRef}
        onChange={handleImageUpload}
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        title="Upload menu image (Max 1MB, JPEG/JPG/PNG/WebP/GIF formats only)"
        className="hidden"
      />

      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Menu Management</h1>
              <p className="text-gray-600 text-sm sm:text-base">Manage your restaurant menu, categories, and items</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                className="px-3 sm:px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-xs sm:text-sm flex items-center justify-center gap-2"
                onClick={() => setShowImportModal(true)}
              >
                <span className="hidden xs:inline sm:inline">Import CSV</span><span className="xs:hidden sm:hidden">Import</span>
              </button>
              <button
                className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-xs sm:text-sm flex items-center justify-center gap-2"
                onClick={downloadSampleCSV}
              >
                <span className="hidden xs:inline sm:inline">Sample CSV</span><span className="xs:hidden sm:hidden">Sample</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex flex-col sm:flex-row -mb-px">
            <button
              className={`py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm transition-colors ${activeTab === 'items'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('items')}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-lg sm:text-xl">🍽️</span>
                <div className="text-left">
                  <div className="font-semibold text-sm sm:text-base">Menu Items</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 sm:gap-2">
                    <span>{menuItems.length} items</span>
                    {pendingCount.total > 0 && (
                      <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {pendingCount.total} pending
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
            <button
              className={`py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm transition-colors ${activeTab === 'categories'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('categories')}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-lg sm:text-xl">📂</span>
                <div className="text-left">
                  <div className="font-semibold text-sm sm:text-base">Categories</div>
                  <div className="text-xs text-gray-500">{categories.length} categories</div>
                </div>
              </div>
            </button>
            <button
              className={`py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm transition-colors ${activeTab === 'subcategories'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('subcategories')}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-lg sm:text-xl">📑</span>
                <div className="text-left">
                  <div className="font-semibold text-sm sm:text-base">Sub-Categories</div>
                  <div className="text-xs text-gray-500">{subCategories.length} sub-categories</div>
                </div>
              </div>
            </button>
            <button
              className={`py-3 sm:py-4 px-3 sm:px-6 border-b-2 font-medium text-xs sm:text-sm transition-colors ${activeTab === 'images'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('images')}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-lg sm:text-xl">🖼️</span>
                <div className="text-left">
                  <div className="font-semibold text-sm sm:text-base">Images</div>
                  <div className="text-xs text-gray-500">{images.length} images</div>
                </div>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Menu Items Tab */}
      {activeTab === 'items' && (
        <>
          {/* Search and Add Button */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="relative w-full lg:max-w-md">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  placeholder="Search menu items..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  data-add-category-btn
                  className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-xs sm:text-sm flex items-center justify-center gap-2 group relative"
                  onClick={() => openModal('category')}
                  title="Add Category (Alt+C)"
                >
                  <span className="hidden xs:inline sm:inline">Add Category</span>
                  <span className="xs:hidden sm:hidden">Category</span>
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Alt+C
                  </span>
                </button>
                <button
                  data-add-sub-category-btn
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-xs sm:text-sm flex items-center justify-center gap-2 group relative"
                  onClick={() => openModal('subcategory')}
                  title="Add Sub-Category (Alt+S)"
                >
                  <span className="hidden xs:inline sm:inline">Add Sub-Category</span>
                  <span className="xs:hidden sm:hidden">Sub-Category</span>
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Alt+S
                  </span>
                </button>
                <button
                  data-add-menu-item-btn
                  className="px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-xs sm:text-sm flex items-center justify-center gap-2 group relative"
                  onClick={() => openModal('item')}
                  title="Add Menu Item (Alt+M)"
                >
                  <span className="hidden xs:inline sm:inline">Add Menu Item</span>
                  <span className="xs:hidden sm:hidden">Menu Item</span>
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Alt+M
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Horizontal Category Filter */}
          <div className="mb-4 bg-white rounded-lg shadow-sm p-3 sm:p-4">
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors ${selectedCategoryFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                onClick={() => {
                  setSelectedCategoryFilter('all');
                  setSelectedSubCategoryFilter('all');
                  setCurrentPage(1);
                  setSubCategoryForm({ ...subCategoryForm, categoryId: '' });
                  setMenuItemForm({ ...menuItemForm, categoryId: '', subCategoryId: '' });
                }}
              >
                All Categories
              </button>
              {categories.map(cat => (
                <button
                  key={cat._id}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors ${selectedCategoryFilter === cat._id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  onClick={() => {
                    setSelectedCategoryFilter(cat._id);
                    setSelectedSubCategoryFilter('all');
                    setCurrentPage(1);
                    setSubCategoryForm({ ...subCategoryForm, categoryId: cat._id });
                    setMenuItemForm({ ...menuItemForm, categoryId: cat._id, subCategoryId: '' });
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Horizontal SubCategory Filter (shows when category selected) */}
          {selectedCategoryFilter !== 'all' && filteredSubCategories.length > 0 && (
            <div className="mb-4 bg-white rounded-lg shadow-sm p-3 sm:p-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                <button
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors ${selectedSubCategoryFilter === 'all'
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  onClick={() => {
                    setSelectedSubCategoryFilter('all');
                    setCurrentPage(1);
                    setMenuItemForm({ ...menuItemForm, categoryId: selectedCategoryFilter, subCategoryId: '' });
                  }}
                >
                  All Subcategories
                </button>
                {filteredSubCategories.map(subCat => (
                  <button
                    key={subCat._id}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors ${selectedSubCategoryFilter === subCat._id
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    onClick={() => {
                      setSelectedSubCategoryFilter(subCat._id);
                      setCurrentPage(1);
                      setMenuItemForm({ ...menuItemForm, categoryId: selectedCategoryFilter, subCategoryId: subCat._id });
                    }}
                  >
                    {subCat.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] sm:min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Recipe</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Availability</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Approval Status</th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div
                          className="relative group cursor-pointer"
                          onClick={() => handleImageClick(item)}
                          title="Click to upload/edit image (Max 1MB, JPEG/JPG/PNG/WebP/GIF)"
                        >
                          <img
                            src={item.images}
                            alt={item.name}
                            className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg border-2 border-gray-200 transition-all group-hover:border-blue-500 group-hover:opacity-75"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-40 rounded-lg">
                            <FiUpload className="text-white text-sm sm:text-base" />
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm font-semibold text-gray-900">{item.name}</div>
                        {item.itemCode && (
                          <div className="text-xs text-gray-500 mt-1 hidden sm:block">itemCode: {item.itemCode}</div>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        {item?.recipe?.ingredients?.length > 0 ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FiPackage className="mr-1" size={10} />
                                {item.recipe.ingredients.length} Items
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 max-w-xs">
                              {item.recipe.ingredients.slice(0, 2).map((ing, idx) => (
                                <div key={idx} className="flex items-center gap-1">
                                  <span className="font-medium">{ing.itemId?.name || ing.name || 'Item'}</span>
                                  <span className="text-gray-400">({ing.quantity} {ing.unit})</span>
                                </div>
                              ))}
                              {item.recipe.ingredients.length > 2 && (
                                <div className="text-gray-400">+{item.recipe.ingredients.length - 2} more...</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <FiPackage className="mr-1" size={10} />
                            No Recipe
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm text-gray-600">{item.categoryId?.name || 'N/A'}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        {item.discountPrice ? (
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center space-x-1 sm:space-x-2">
                              <span className="text-xs sm:text-sm font-bold text-green-600">₹{item.discountPrice}</span>
                              <span className="text-xs sm:text-sm text-gray-400 line-through hidden sm:inline">₹{item.price}</span>
                            </div>
                            {item.pricingUnit && (
                              <span className="text-xs text-gray-500">{item.pricingUnit}</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col space-y-1">
                            <span className="text-xs sm:text-sm font-bold text-gray-900">₹{item.price}</span>
                            {item.pricingUnit && (
                              <span className="text-xs text-gray-500">{item.pricingUnit}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isVeg
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {item.isVeg ? 'Veg' : 'Non-Veg'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isAvailable
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {item.isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                        {getApprovalStatusBadge(item)}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1 sm:space-y-2">
                          <div className="flex gap-1 sm:gap-2">
                            <button
                              className="px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs sm:text-sm rounded hover:bg-blue-700 transition-colors"
                              onClick={() => openModal('item', item)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-2 sm:px-3 py-1 bg-gray-600 text-white text-xs sm:text-sm rounded hover:bg-gray-700 transition-colors flex items-center gap-1"
                              onClick={() => fetchItemStatus(item._id)}
                            >
                              <span className="hidden sm:inline">View</span>
                              <span className="sm:hidden">View</span>
                            </button>
                          </div>
                          {item?.recipe?.ingredients?.length > 0 ? (
                            <button
                              className="px-2 sm:px-3 py-1 bg-green-600 text-white text-xs sm:text-sm rounded hover:bg-green-700 transition-colors flex items-center gap-1"
                              onClick={() => openRecipeModal(item)}
                            >
                              <span className="hidden sm:inline">Recipe</span>
                              <span className="sm:hidden">Recipe</span>
                            </button>
                          ) : (
                            <button
                              className="px-2 sm:px-3 py-1 bg-orange-600 text-white text-xs sm:text-sm rounded hover:bg-orange-700 transition-colors flex items-center gap-1"
                              onClick={() => openRecipeModal(item)}
                            >
                              <span className="hidden sm:inline">Recipe</span>
                              <span className="sm:hidden">Recipe</span>
                            </button>
                          )}
                          <button
                            className="px-2 sm:px-3 py-1 bg-red-600 text-white text-xs sm:text-sm rounded hover:bg-red-700 transition-colors"
                            onClick={() => handleDelete('item', item._id)}
                          >
                            <span className="hidden sm:inline">Delete</span>
                            <span className="sm:hidden">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredItems.length === 0 && (
              <div className="text-center py-8 sm:py-12">
                <div className="text-gray-400 text-base sm:text-lg">No menu items found</div>
                <div className="text-gray-500 text-xs sm:text-sm mt-2">Try adjusting your search or filters</div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm mt-6 p-3 sm:p-4 border-t border-gray-200">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  Showing {menuItems.length} of {totalItems} items
                </div>
                <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button
                      className="px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">‹</span>
                    </button>
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm border rounded-lg transition-colors min-w-[28px] sm:min-w-[36px] ${pageNum === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">...</span>;
                    }
                    return null;
                  })}
                  <button
                    className="px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <span className="sm:hidden">›</span>
                  </button>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm justify-center">
                  <span className="text-gray-600">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 sm:px-3 sm:py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-xs sm:text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-gray-600">per page</span>
                </div>
              </div>
            </div>
            </div>
          )}
        </>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
              onClick={() => openModal('category')}
            >
              Add Category
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST %</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.slice((categoryPage - 1) * categoryItemsPerPage, categoryPage * categoryItemsPerPage).map((cat) => (
                    <tr key={cat._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{cat.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{cat.description || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {cat.gstPercentage}% <span className="text-gray-500">({cat.isGSTInclude ? 'Include' : 'Exclude'})</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cat.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {cat.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex flex-col space-y-2 items-end">
                          <button
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                            onClick={() => {
                              setMenuItemForm({ ...menuItemForm, categoryId: cat._id, subCategoryId: '' });
                              openModal('item');
                            }}
                          >
                            Add Menu
                          </button>
                          <div className="flex space-x-2">
                            <button
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                              onClick={() => {
                                setSubCategoryForm({ ...subCategoryForm, categoryId: cat._id });
                                openModal('subcategory');
                              }}
                            >
                              Add Subcategory
                            </button>
                            <button
                              className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                              onClick={() => openModal('category', cat)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                              onClick={() => handleDelete('category', cat._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination for Categories */}
          {Math.ceil(categories.length / categoryItemsPerPage) > 1 && (
            <div className="bg-white rounded-lg shadow-sm mt-6 p-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing {Math.min((categoryPage - 1) * categoryItemsPerPage + 1, categories.length)}-{Math.min(categoryPage * categoryItemsPerPage, categories.length)} of {categories.length} categories
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setCategoryPage(prev => Math.max(1, prev - 1))}
                    disabled={categoryPage === 1}
                  >
                    Previous
                  </button>
                  {[...Array(Math.ceil(categories.length / categoryItemsPerPage))].map((_, idx) => {
                    const pageNum = idx + 1;
                    const totalCatPages = Math.ceil(categories.length / categoryItemsPerPage);
                    if (
                      pageNum === 1 ||
                      pageNum === totalCatPages ||
                      (pageNum >= categoryPage - 1 && pageNum <= categoryPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-2 text-sm border rounded-lg transition-colors min-w-[36px] ${pageNum === categoryPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          onClick={() => setCategoryPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === categoryPage - 2 || pageNum === categoryPage + 2) {
                      return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  })}
                  <button
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setCategoryPage(prev => Math.min(Math.ceil(categories.length / categoryItemsPerPage), prev + 1))}
                    disabled={categoryPage === Math.ceil(categories.length / categoryItemsPerPage)}
                  >
                    Next
                  </button>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-600">Show</span>
                  <select
                    value={categoryItemsPerPage}
                    onChange={(e) => {
                      setCategoryItemsPerPage(Number(e.target.value));
                      setCategoryPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-gray-600">per page</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Sub-Categories Tab */}
      {activeTab === 'subcategories' && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              onClick={() => openModal('subcategory')}
            >
              Add Sub-Category
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subCategories.slice((subCategoryPage - 1) * subCategoryItemsPerPage, subCategoryPage * subCategoryItemsPerPage).map((subCat) => (
                    <tr key={subCat._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{subCat.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{subCat.categoryId?.name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{subCat.description || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${subCat.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {subCat.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-2">
                          <button
                            className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                            onClick={() => {
                              setMenuItemForm({
                                ...menuItemForm,
                                categoryId: subCat.categoryId?._id || subCat.categoryId,
                                subCategoryId: subCat._id
                              });
                              openModal('item');
                            }}
                            title="Add Menu Item"
                          >
                            Add Item
                          </button>
                          <div className="flex space-x-2">
                            <button
                              className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                              onClick={() => openModal('subcategory', subCat)}
                            >
                              Edit
                            </button>
                            <button
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                              onClick={() => handleDelete('subcategory', subCat._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination for SubCategories */}
          {Math.ceil(subCategories.length / subCategoryItemsPerPage) > 1 && (
            <div className="bg-white rounded-lg shadow-sm mt-6 p-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing {Math.min((subCategoryPage - 1) * subCategoryItemsPerPage + 1, subCategories.length)}-{Math.min(subCategoryPage * subCategoryItemsPerPage, subCategories.length)} of {subCategories.length} subcategories
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setSubCategoryPage(prev => Math.max(1, prev - 1))}
                    disabled={subCategoryPage === 1}
                  >
                    Previous
                  </button>
                  {[...Array(Math.ceil(subCategories.length / subCategoryItemsPerPage))].map((_, idx) => {
                    const pageNum = idx + 1;
                    const totalSubCatPages = Math.ceil(subCategories.length / subCategoryItemsPerPage);
                    if (
                      pageNum === 1 ||
                      pageNum === totalSubCatPages ||
                      (pageNum >= subCategoryPage - 1 && pageNum <= subCategoryPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-2 text-sm border rounded-lg transition-colors min-w-[36px] ${pageNum === subCategoryPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          onClick={() => setSubCategoryPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === subCategoryPage - 2 || pageNum === subCategoryPage + 2) {
                      return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  })}
                  <button
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={() => setSubCategoryPage(prev => Math.min(Math.ceil(subCategories.length / subCategoryItemsPerPage), prev + 1))}
                    disabled={subCategoryPage === Math.ceil(subCategories.length / subCategoryItemsPerPage)}
                  >
                    Next
                  </button>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-600">Show</span>
                  <select
                    value={subCategoryItemsPerPage}
                    onChange={(e) => {
                      setSubCategoryItemsPerPage(Number(e.target.value));
                      setSubCategoryPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-gray-600">per page</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <>
          {/* Header with Search and Actions */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
              {/* Search Bar */}
              <div className="relative w-full lg:max-w-md">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Search images..."
                  value={imageSearchTerm}
                  onChange={(e) => setImageSearchTerm(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
                >
                  <FiUpload /> Upload Images
                </button>
                
                <button
                  onClick={fetchUnusedImages}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm flex items-center gap-2"
                >
                  <FiFilter /> Clean Images
                </button>

                {selectedImages.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm flex items-center gap-2"
                  >
                    <FiTrash2 /> Delete Selected ({selectedImages.length})
                  </button>
                )}

                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setImageViewMode('grid')}
                    className={`px-3 py-1 rounded transition-colors ${
                      imageViewMode === 'grid'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <FiGrid />
                  </button>
                  <button
                    onClick={() => setImageViewMode('list')}
                    className={`px-3 py-1 rounded transition-colors ${
                      imageViewMode === 'list'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <FiList />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Unused Images Header */}
          {showUnusedImages && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 mb-1">
                    Unused Images ({unusedImages.length})
                  </h3>
                  <p className="text-sm text-yellow-700">
                    These images are not referenced in any menu items. They can be safely deleted.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowUnusedImages(false);
                    setSelectedImages([]);
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium text-sm"
                >
                  Back to All Images
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loadingImages || loadingUnused ? (
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            </div>
          ) : (
            <>
              {/* Grid View */}
              {imageViewMode === 'grid' && (
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  {/* Select All Checkbox */}
                  {filteredImages.length > 0 && (
                    <div className="mb-4 flex items-center">
                      <input
                        type="checkbox"
                        checked={filteredImages.every(img => selectedImages.includes(img._id))}
                        onChange={toggleAllImageSelection}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-600">Select All</label>
                    </div>
                  )}

                  {/* Images Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredImages.map((image) => (
                      <div
                        key={image._id}
                        className={`relative group border rounded-lg overflow-hidden transition-all hover:shadow-lg ${
                          showUnusedImages ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        }`}
                      >
                        {/* Selection Checkbox */}
                        <div className="absolute top-2 left-2 z-10">
                          <input
                            type="checkbox"
                            checked={selectedImages.includes(image._id)}
                            onChange={() => toggleImageSelection(image._id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>

                        {/* Unused Badge */}
                        {showUnusedImages && (
                          <div className="absolute top-2 right-2 z-10">
                            <span className="px-2 py-1 bg-red-600 text-white text-xs font-semibold rounded-full">
                              Unused
                            </span>
                          </div>
                        )}

                        {/* Image */}
                        <div className="aspect-square relative">
                          <img
                            src={image.url}
                            alt={image.originalName || image.filename}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleDownload(image)}
                                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                title="Download"
                              >
                                <FiDownload className="text-green-600" />
                              </button>
                              <button
                                onClick={() => {
                                  setRenamingImage(image);
                                  setNewFilename(image.originalName || image.filename);
                                  setShowRenameModal(true);
                                }}
                                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                title="Rename"
                              >
                                <FiEdit className="text-blue-600" />
                              </button>
                              <button
                                onClick={() => handleDeleteImage(image._id)}
                                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                title="Delete"
                              >
                                <FiTrash2 className="text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Image Info */}
                        <div className="p-3">
                          <p className="text-sm font-medium text-gray-900 truncate" title={image.originalName || image.filename}>
                            {image.originalName || image.filename}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(image.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Empty State */}
                  {filteredImages.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-6xl mb-4">
                        {showUnusedImages ? '🗑️' : '🖼️'}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {showUnusedImages ? 'No Unused Images' : 'No Images Found'}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {showUnusedImages
                          ? 'All images are being used in menu items.'
                          : 'Upload some images to get started.'}
                      </p>
                      {!showUnusedImages && (
                        <button
                          onClick={() => setShowUploadModal(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                        >
                          Upload Images
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* List View */}
              {imageViewMode === 'list' && (
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={filteredImages.every(img => selectedImages.includes(img._id))}
                              onChange={toggleAllImageSelection}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Image
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Size
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredImages.map((image) => (
                          <tr key={image._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedImages.includes(image._id)}
                                onChange={() => toggleImageSelection(image._id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <img
                                src={image.url}
                                alt={image.originalName || image.filename}
                                className="h-12 w-12 object-cover rounded-lg"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {image.originalName || image.filename}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">
                                {(image.size / 1024).toFixed(1)} KB
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {showUnusedImages ? (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  Unused
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  In Use
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleDownload(image)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Download"
                                >
                                  <FiDownload />
                                </button>
                                <button
                                  onClick={() => {
                                    setRenamingImage(image);
                                    setNewFilename(image.originalName || image.filename);
                                    setShowRenameModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Rename"
                                >
                                  <FiEdit />
                                </button>
                                <button
                                  onClick={() => handleDeleteImage(image._id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Empty State for List View */}
                  {filteredImages.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-6xl mb-4">
                        {showUnusedImages ? '🗑️' : '🖼️'}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {showUnusedImages ? 'No Unused Images' : 'No Images Found'}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {showUnusedImages
                          ? 'All images are being used in menu items.'
                          : 'Upload some images to get started.'}
                      </p>
                      {!showUnusedImages && (
                        <button
                          onClick={() => setShowUploadModal(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                        >
                          Upload Images
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Image Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Upload Images</h2>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                onClick={() => setShowUploadModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <div className="mb-4">
                  <label className="cursor-pointer">
                    <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm">
                      Select Files
                    </span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageManagementUpload}
                      className="hidden"
                      disabled={uploadingImages}
                    />
                  </label>
                  <p className="text-sm text-gray-600 mt-2">
                    or drag and drop images here
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  Maximum file size: 1MB per image
                </p>
              </div>
              {uploadingImages && (
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-sm text-gray-600">Uploading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Rename Image</h2>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setShowRenameModal(false);
                  setRenamingImage(null);
                  setNewFilename('');
                }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Filename
                </label>
                <input
                  type="text"
                  value={newFilename}
                  onChange={(e) => setNewFilename(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter new filename"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRenameModal(false);
                    setRenamingImage(null);
                    setNewFilename('');
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRenameImage(renamingImage)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && modalType === 'category' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto m-2 sm:m-0" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{selectedItem ? 'Edit Category' : 'Add Category'}</h2>
              <button
                className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowModal(false)}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateCategory}>
              <div className="px-4 sm:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Category Name *</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Description</label>
                  <textarea
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">GST Percentage *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={categoryForm.gstPercentage}
                      inputMode="decimal"
                      placeholder="GST %"
                      required
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9.]/g, '');

                        // allow only one decimal point
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }

                        // limit 0–100
                        if (Number(value) > 100) {
                          value = '100';
                        }

                        setCategoryForm({
                          ...categoryForm,
                          gstPercentage: value
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={categoryForm.sortOrder}
                      inputMode="numeric"
                      placeholder="Sort order"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // only numbers
                        setCategoryForm({
                          ...categoryForm,
                          sortOrder: value
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Active</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={categoryForm.isActive}
                      onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">GST Included in Price</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={categoryForm.isGSTInclude}
                      onChange={(e) => setCategoryForm({ ...categoryForm, isGSTInclude: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
              <div className="border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  className="px-3 sm:px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  {selectedItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && modalType === 'subcategory' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto m-2 sm:m-0" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{selectedItem ? 'Edit Sub-Category' : 'Add Sub-Category'}</h2>
              <button
                className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowModal(false)}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateSubCategory}>
              <div className="px-4 sm:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Category *</label>
                  <div className="flex gap-2">
                    <select
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={subCategoryForm.categoryId}
                      onChange={(e) => setSubCategoryForm({ ...subCategoryForm, categoryId: e.target.value })}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      onClick={() => setShowQuickCreate(!showQuickCreate)}
                      title="Quick Create Category"
                    >
                      <span className="text-sm sm:text-base">+</span>
                    </button>
                  </div>
                  {showQuickCreate && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                          placeholder="New category name"
                          value={quickCategoryName}
                          onChange={(e) => setQuickCategoryName(e.target.value)}
                        />
                        <button
                          type="button"
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm font-medium"
                          onClick={handleQuickCreateCategory}
                        >
                          Create
                        </button>
                        <button
                          type="button"
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium"
                          onClick={() => {
                            setShowQuickCreate(false);
                            setQuickCategoryName('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Category Name *</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    value={subCategoryForm.name}
                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                    value={subCategoryForm.description}
                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Active</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={subCategoryForm.isActive}
                      onChange={(e) => setSubCategoryForm({ ...subCategoryForm, isActive: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
              <div className="border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  className="px-3 sm:px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  {selectedItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && modalType === 'item' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-2 sm:m-0" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{selectedItem ? 'Edit Menu Item' : 'Add Menu Item'}</h2>
              <button
                className="absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowModal(false)}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateMenuItem}>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                    <div className="flex gap-2">
                      <select
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        value={menuItemForm.categoryId}
                        onChange={(e) => setMenuItemForm({ ...menuItemForm, categoryId: e.target.value })}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        onClick={() => {
                          setShowQuickCreate(!showQuickCreate);
                          setQuickCreateType('category');
                        }}
                        title="Quick Create Category"
                      >
                        <span className="text-base">+</span>
                      </button>
                    </div>
                    {showQuickCreate && quickCreateType === 'category' && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                            placeholder="New category name"
                            value={quickCategoryName}
                            onChange={(e) => setQuickCategoryName(e.target.value)}
                          />
                          <button
                            type="button"
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                            onClick={handleQuickCreateCategory}
                          >
                            Create
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                            onClick={() => {
                              setShowQuickCreate(false);
                              setQuickCategoryName('');
                              setQuickCreateType('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Category (Optional)</label>
                    <div className="flex gap-2">
                      <select
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        value={menuItemForm.subCategoryId}
                        onChange={(e) => setMenuItemForm({ ...menuItemForm, subCategoryId: e.target.value })}
                      >
                        <option value="">Select Sub-Category (Optional)</option>
                        {subCategories
                          .filter(sc => (sc.categoryId?._id || sc.categoryId) === menuItemForm.categoryId)
                          .map((subCat) => (
                            <option key={subCat._id} value={subCat._id}>{subCat.name}</option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => {
                          setShowQuickCreate(!showQuickCreate);
                          setQuickCreateType('subcategory');
                        }}
                        title="Quick Create Sub-Category"
                        disabled={!menuItemForm.categoryId}
                      >
                        <span className="text-base">+</span>
                      </button>
                    </div>
                    {showQuickCreate && quickCreateType === 'subcategory' && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                            placeholder="New sub-category name"
                            value={quickSubCategoryName}
                            onChange={(e) => setQuickSubCategoryName(e.target.value)}
                          />
                          <button
                            type="button"
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                            onClick={handleQuickCreateSubCategory}
                          >
                            Create
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                            onClick={() => {
                              setShowQuickCreate(false);
                              setQuickSubCategoryName('');
                              setQuickCreateType('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Name *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={menuItemForm.name}
                      onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Code</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={menuItemForm.itemCode}
                      onChange={(e) => setMenuItemForm({ ...menuItemForm, itemCode: e.target.value })}
                      placeholder="Enter Item Code"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors resize-none"
                    value={menuItemForm.description}
                    onChange={(e) => setMenuItemForm({ ...menuItemForm, description: e.target.value })}
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Item Image (Optional)</label>
                  <input
                    type="file"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    title="Upload menu image (Max 1MB, JPEG/JPG/PNG/WebP/GIF formats only)"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Validate file type
                        if (!file.type.startsWith('image/')) {
                          toast.error('Please select an image file (JPEG, JPG, PNG, WebP, GIF)');
                          e.target.value = '';
                          return;
                        }

                        // Validate file size (max 1MB)
                        if (file.size > 1 * 1024 * 1024) {
                          toast.error('Image size should be less than 1MB');
                          e.target.value = '';
                          return;
                        }
                      }
                      setMenuItemForm({ ...menuItemForm, image: file });
                    }}
                  />
                  {selectedItem && selectedItem.images ? (
                    <p className="text-sm text-gray-500 mt-2">
                      Current image will be replaced if you upload a new one (Max 1MB, JPEG/JPG/PNG/WebP/GIF)
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2">
                      Upload menu image (Max 1MB, JPEG/JPG/PNG/WebP/GIF formats only)
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={menuItemForm.price}
                      inputMode="decimal"
                      placeholder="Enter price"
                      required
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9.]/g, '');

                        // allow only one decimal point
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }

                        setMenuItemForm({
                          ...menuItemForm,
                          price: value
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Unit</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={menuItemForm.pricingUnit}
                      placeholder="e.g., per kg, per piece, per plate"
                      onChange={(e) => {
                        setMenuItemForm({
                          ...menuItemForm,
                          pricingUnit: e.target.value
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Discount Price</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={menuItemForm.discountPrice}
                      inputMode="decimal"
                      placeholder="Enter discount price"
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9.]/g, '');

                        // allow only one decimal point
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }

                        setMenuItemForm({
                          ...menuItemForm,
                          discountPrice: value
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 items-end">
                  <div className="flex items-center justify-start gap-3">
                    <label className="text-sm font-medium text-gray-700">Vegetarian</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={menuItemForm.isVeg}
                        onChange={(e) => setMenuItemForm({ ...menuItemForm, isVeg: e.target.checked })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-start gap-3">
                    <label className="text-sm font-medium text-gray-700">Available</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={menuItemForm.isAvailable}
                        onChange={(e) => setMenuItemForm({ ...menuItemForm, isAvailable: e.target.checked })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prep Time (min)</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      value={menuItemForm.preparationTime}
                      inputMode="numeric"
                      placeholder="Preparation time (min)"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // only digits
                        setMenuItemForm({
                          ...menuItemForm,
                          preparationTime: value
                        });
                      }}
                    />
                  </div>
                </div>

                {/* Recipe Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white text-sm font-bold">R</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Recipe</h3>
                    </div>
                    {selectedItem ? (
                      <button
                        type="button"
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                        onClick={() => openRecipeModal(selectedItem)}
                      >
                        {selectedItem?.recipe?.ingredients?.length > 0 ? 'Edit Recipe' : 'Add Recipe'}
                      </button>
                    ) : (
                      <div className="text-sm text-gray-500 italic">
                        Save menu item first to add recipe
                      </div>
                    )}
                  </div>

                  {selectedItem?.recipe?.ingredients?.length > 0 && (
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <h4 className="font-medium text-gray-900 mb-3">Current Recipe Ingredients:</h4>
                      <div className="space-y-2">
                        {selectedItem.recipe.ingredients.map((ingredient, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-orange-200">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-900">{ingredient?.itemId?.name}</div>
                            </div>
                            <div className="text-xs text-orange-600 font-medium">
                              {ingredient.quantity} {ingredient.unit}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
                <button
                  type="button"
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {selectedItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Recipe Modal */}
      {showRecipeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 relative flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <FiPackage className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {selectedMenuItemForRecipe?.recipe?.ingredients?.length > 0 ? 'Edit Recipe' : 'Create Recipe'}
                  </h2>
                  <p className="text-orange-100 text-sm">
                    {selectedMenuItemForRecipe?.recipe?.ingredients?.length > 0
                      ? `Editing recipe for: ${selectedMenuItemForRecipe?.name}`
                      : `Creating recipe for: ${selectedMenuItemForRecipe?.name}`
                    }
                  </p>
                </div>
              </div>
              <button
                className="absolute top-6 right-6 text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                onClick={closeRecipeModal}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Category Selection */}
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                    <FiPackage className="text-white text-sm" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Select Inventory Category</h3>
                </div>
                <select
                  className="w-full px-4 py-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500 focus:ring-opacity-20 transition-all outline-none bg-white"
                  value={selectedInventoryCategory}
                  onChange={(e) => setSelectedInventoryCategory(e.target.value)}
                >
                  <option value="">📂 Choose a category...</option>
                  {inventoryCategories.filter(cat => cat.isActive).map((cat) => (
                    <option key={cat._id} value={cat._id}>📁 {cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Available Items */}
              {selectedInventoryCategory && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                        <FiPlus className="text-white text-sm" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Available Ingredients</h3>
                    </div>
                    <div className="text-sm text-blue-600 font-medium">
                      {inventoryItems.length} items found
                    </div>
                  </div>

                  {loadingInventory ? (
                    <div className="text-center py-8">
                      <div className="inline-flex items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-blue-600">Loading ingredients...</span>
                      </div>
                    </div>
                  ) : inventoryItems.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-80  pr-2">
                      {inventoryItems.map((item) => {
                        const isAdded = recipeItems.some(recipeItem => (recipeItem.itemId === item._id || recipeItem?.itemId._id === item._id));
                        console.log(recipeItems, "recipeItemsrecipeItems", isAdded)

                        // console.log(`Checking item ${item.name} (${item._id}): isAdded = ${isAdded}, recipeItems = ${recipeItems.map(ri => ri.itemId).join(', ')}`);
                        return (
                          <div
                            key={item._id}
                            className={`relative group cursor-pointer transition-all duration-200 ${isAdded
                              ? 'opacity-60 cursor-not-allowed'
                              : 'hover:scale-105 hover:shadow-lg'
                              }`}
                            onClick={() => !isAdded && addRecipeItem(item)}
                          >
                            <div className={`bg-white rounded-xl border-2 p-4 h-full ${isAdded
                              ? 'border-gray-300 bg-gray-50'
                              : 'border-blue-200 hover:border-blue-400 hover:bg-blue-50'
                              }`}>
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.name}</h4>
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${(item.quantity || 0) > 10
                                      ? 'bg-green-100 text-green-800'
                                      : (item.quantity || 0) > 0
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-red-100 text-red-800'
                                      }`}>
                                      📦Stock: {(item.quantity || 0).toFixed(2)} {item.unit || 'pcs'}
                                    </span>
                                  </div>
                                </div>
                                {isAdded ? (
                                  <div className="flex items-center gap-1 text-green-600">
                                    <FiCheck size={16} />
                                    <span className="text-xs font-medium">Added</span>
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                                    <FiPlus className="text-white text-sm" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiPackage className="text-gray-400 text-2xl" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">No ingredients found</h4>
                      <p className="text-gray-600">Try selecting a different category</p>
                    </div>
                  )}
                </div>
              )}

              {/* Recipe Items */}
              {recipeItems.length > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                        <FiPackage className="text-white text-sm" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Recipe Ingredients</h3>
                    </div>
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {recipeItems.length} ingredients
                    </div>
                  </div>

                  <div className="space-y-3">
                    {recipeItems.map((recipeItem, index) => (
                      <div key={recipeItem.itemId} className="bg-white rounded-xl border-2 border-green-200 p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-2">{recipeItem.name}</h4>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                                <label className="text-sm font-medium text-gray-700">Quantity:</label>
                                <input
                                  type="text"
                                  value={recipeItem.quantity}
                                  onChange={(e) => {
                                    const rawValue = e.target.value;

                                    // Allow only numbers + decimal
                                    const numericValue = rawValue.replace(/[^0-9.]/g, '');

                                    updateRecipeItem(
                                      recipeItem.itemId,
                                      'quantity',
                                      numericValue === '' ? '' : Number(numericValue)
                                    );
                                  }}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none text-center"
                                />

                                {/* Unit Selection Radio Buttons */}
                                {recipeItem.originalUnit && getConversionOptions(recipeItem.originalUnit).length > 1 ? (
                                  <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200">
                                    {getConversionOptions(recipeItem.originalUnit).map((option) => (
                                      <label key={option.value} className="flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors">
                                        <input
                                          type="radio"
                                          name={`unit-${recipeItem.itemId}-${index}`}
                                          value={option.value}
                                          checked={recipeItem.unit === option.value}
                                          onChange={() => {
                                            // Convert quantity based on the factor
                                            const currentOption = getConversionOptions(recipeItem.unit).find(opt => opt.value === recipeItem.unit);
                                            const newQuantity = currentOption ? (recipeItem.quantity / currentOption.factor) * option.factor : recipeItem.quantity;

                                            updateRecipeItem(recipeItem.itemId, 'quantity', newQuantity);
                                            updateRecipeItem(recipeItem.itemId, 'unit', option.value);
                                          }}
                                          className="text-green-600 focus:ring-green-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{option.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-sm font-medium text-gray-600 bg-white px-2 py-1 rounded">
                                    {recipeItem.unit}
                                  </span>
                                )}
                              </div>
                              {/* <div className="text-xs text-gray-500">
                                Available: {recipeItem.quantity} {recipeItem.originalUnit || recipeItem.unit}
                              </div> */}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeRecipeItem(recipeItem.itemId)}
                            className="w-10 h-10 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center group"
                            title="Remove ingredient"
                          >
                            <FiX size={18} className="group-hover:scale-110 transition-transform" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {recipeItems.length === 0 && selectedInventoryCategory && (
                <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiPackage className="text-gray-400 text-2xl" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No ingredients added yet</h4>
                  <p className="text-gray-600 mb-4">Click on ingredients above to add them to your recipe</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
              <div className="text-sm text-gray-600">
                {recipeItems.length > 0 && (
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{recipeItems.length}</span> ingredients selected
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
                  onClick={closeRecipeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg ${recipeItems.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                    }`}
                  onClick={saveRecipe}
                  disabled={recipeItems.length === 0}
                >
                  {recipeItems.length === 0 ? 'Add Ingredients First' : 'Save Recipe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <FiUpload className="text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Import Menu Items</h2>
                    <p className="text-orange-100">Upload CSV file to import menu items in bulk</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <ol className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start gap-2">
                      <span className="font-bold">1.</span>
                      <span>Download the sample CSV file to understand the format</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">2.</span>
                      <span>Fill your menu data in the CSV file with correct column headers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">3.</span>
                      <span>Categories and subcategories will be created automatically if they don't exist</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">4.</span>
                      <span>Upload the filled CSV file to import your menu items</span>
                    </li>
                  </ol>
                </div>
              </div>

              <div className="mb-6">
                <button
                  onClick={downloadSampleCSV}
                  className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <FiDownload />
                  Download Sample CSV
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <div className="relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                  />
                  {csvFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                      ✅ {csvFile.name} selected
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">CSV Format Required:</h4>
                <div className="text-xs text-yellow-700 font-mono bg-white rounded-lg p-3 overflow-x-auto">
                  Category, Sub Category (optional), Item Name, Description, Price, Discount Price, Is Veg, Is Available, Preparation Time (minutes), Sort Order
                </div>
                <div className="mt-2 text-xs text-yellow-600">
                  <p>💡 <strong>Tip:</strong> Leave Sub Category empty if not needed for the item</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
              <div className="text-sm text-gray-600">
                {csvFile && (
                  <span className="flex items-center gap-2">
                    <span className="font-medium">File:</span> {csvFile.name}
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg flex items-center gap-2 ${!csvFile || importing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                    }`}
                  onClick={importMenuItems}
                  disabled={!csvFile || importing}
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <FiUpload />
                      Import Menu Items
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <FiX className="text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Import Errors</h2>
                    <p className="text-red-100">{importErrors.length} items failed to import</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowErrorModal(false);
                    setImportErrors([]);
                  }}
                  className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-800">
                    <strong>⚠️ Important:</strong> Please review the errors below and fix the issues in your CSV file before re-importing.
                  </p>
                </div>
              </div>

              {/* Error List */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Row</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Sub Category</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">itemCode</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {importErrors.map((error, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {error.row}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">{error.itemName}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{error.category}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{error.subCategory}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-medium">{error.itemCode}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">₹{error.price}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {error.error}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Common Issues */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-semibold text-blue-800 mb-3">🔧 Common Issues & Solutions:</h4>
                <div className="space-y-2 text-sm text-blue-700">
                  <div className="flex items-start gap-2">
                    <span className="font-bold">•</span>
                    <span><strong>Invalid Price:</strong> Ensure price is a valid number (e.g., 25.50)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold">•</span>
                    <span><strong>Missing Category:</strong> Category name cannot be empty</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold">•</span>
                    <span><strong>Invalid Boolean:</strong> Use 'true' or 'false' for Is Veg and Is Available</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold">•</span>
                    <span><strong>Duplicate Item:</strong> Item name must be unique within the same category</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg"
                onClick={() => {
                  setShowErrorModal(false);
                  setImportErrors([]);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && selectedStatusItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Approval Status Details</h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>

              {loadingStatus ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Current Status */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Current Status</h4>
                    <div className="space-y-2">
                      {getApprovalStatusBadge(selectedStatusItem)}
                    </div>
                  </div>

                  {/* Creation Status */}
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Creation Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Approved:</span>
                        <span className={`font-medium ${selectedStatusItem.isApproved ? 'text-green-600' : 'text-yellow-600'}`}>
                          {selectedStatusItem.isApproved ? 'Yes' : 'Pending'}
                        </span>
                      </div>
                      {selectedStatusItem.approvedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Approved At:</span>
                          <span className="text-gray-900">
                            {new Date(selectedStatusItem.approvedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {selectedStatusItem.approvedBy && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Approved By:</span>
                          <span className="text-gray-900">
                            {selectedStatusItem.approvedBy?.name || 'Vendor Owner'}
                          </span>
                        </div>
                      )}
                      {selectedStatusItem.rejectionReason && (
                        <div className="mt-2">
                          <span className="text-gray-600">Rejection Reason:</span>
                          <p className="text-red-600 mt-1">{selectedStatusItem.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Update Status */}
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Update Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pending Update:</span>
                        <span className={`font-medium ${selectedStatusItem.hasPendingUpdate ? 'text-orange-600' : 'text-green-600'}`}>
                          {selectedStatusItem.hasPendingUpdate ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {selectedStatusItem.hasPendingUpdate && selectedStatusItem.pendingUpdateRequestedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Requested At:</span>
                          <span className="text-gray-900">
                            {new Date(selectedStatusItem.pendingUpdateRequestedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {selectedStatusItem.hasPendingUpdate && selectedStatusItem.pendingUpdateRequestedBy && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Requested By:</span>
                          <span className="text-gray-900">
                            {selectedStatusItem.pendingUpdateRequestedBy?.name || 'Vendor'}
                          </span>
                        </div>
                      )}
                      {selectedStatusItem.pendingUpdateRejectionReason && (
                        <div className="mt-2">
                          <span className="text-gray-600">Rejection Reason:</span>
                          <p className="text-red-600 mt-1">{selectedStatusItem.pendingUpdateRejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delete Status */}
                  <div className="border-l-4 border-red-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Delete Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pending Delete:</span>
                        <span className={`font-medium ${selectedStatusItem.hasPendingDelete ? 'text-red-600' : 'text-green-600'}`}>
                          {selectedStatusItem.hasPendingDelete ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {selectedStatusItem.hasPendingDelete && selectedStatusItem.pendingDeleteRequestedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Requested At:</span>
                          <span className="text-gray-900">
                            {new Date(selectedStatusItem.pendingDeleteRequestedAt).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {selectedStatusItem.hasPendingDelete && selectedStatusItem.pendingDeleteRequestedBy && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Requested By:</span>
                          <span className="text-gray-900">
                            {selectedStatusItem.pendingDeleteRequestedBy?.name || 'Vendor'}
                          </span>
                        </div>
                      )}
                      {selectedStatusItem.pendingDeleteRejectionReason && (
                        <div className="mt-2">
                          <span className="text-gray-600">Rejection Reason:</span>
                          <p className="text-red-600 mt-1">{selectedStatusItem.pendingDeleteRejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
